import RestService from '@core/services/game/_private/RestService';
import CasinoLiveMapper from '@core/mappers/game/CasinoLiveMapper';
import GameMapper from '@core/mappers/game/GameMapper';
import VirtualMapper from '@core/mappers/game/VirtualMapper';
import XJackpotDetailsMapper from '@core/mappers/game/XJackpotDetailsMapper';
import FavoriteMapper from '@core/mappers/game/FavoriteMapper';
import JackpotBannerMapper from '@core/mappers/game/JackpotBannerMapper';
import CasinoGameLaunchParamsMapper from '@core/mappers/game/CasinoGameLaunchParamsMapper';
import * as NetworkUtils from '@utils/network/NetworkUtils';
import XJackpotDetails from '@core/models/games/XJackpotDetails';
import { isMobile } from '@core/utils/userAgent/UAUtils';
import { Game } from '@core/models/games/Game';
import Virtual from '@core/models/games/Virtual';

export enum GameCategoryMapRO {
    SLOTURI = 'slots',
    JACKPOT = 'jackpot',
    NOU = 'new',
    'JOCURI DE MASĂ' = 'table',
    RECOMANDATE = 'recommended',
    Numere = 'numbers',
    Sport = 'sport',
    casinoLive = 'casinoLive',
}

export enum GameCategory {
    favourites = 'favourites',
    slots = 'slots',
    jackpot = 'jackpot',
    recommended = 'recommended',
    new = 'new',
    table = 'table',
    casinoLive = 'casino live',
    sport = 'sport',
    numbers = 'numbers',
}

const mapCountryKey = (key: GameCategoryMapRO) => {
    // @ts-ignore
    return GameCategoryMapRO[key as any];
};

export default class GameService {
    private readonly restService = new RestService();
    private readonly gameMapper = new GameMapper();
    private readonly casinoLiveMapper = new CasinoLiveMapper();
    private readonly virtualMapper = new VirtualMapper();
    private readonly favoriteMapper = new FavoriteMapper();
    private readonly jackpotBannerMapper = new JackpotBannerMapper();
    private readonly xJackpotDetailsMapper = new XJackpotDetailsMapper();
    private readonly casinoGameLaunchParamsMapper = new CasinoGameLaunchParamsMapper();
    private categoryMappers: {
        [GameCategory.slots]: GameMapper,
        [GameCategory.jackpot]: GameMapper,
        [GameCategory.new]: GameMapper,
        [GameCategory.table]: GameMapper,
        [GameCategory.recommended]: GameMapper,
        [GameCategory.numbers]: VirtualMapper,
        [GameCategory.sport]: VirtualMapper,
    } = {
        [GameCategory.slots]: this.gameMapper,
        [GameCategory.jackpot]: this.gameMapper,
        [GameCategory.new]: this.gameMapper,
        [GameCategory.table]: this.gameMapper,
        [GameCategory.recommended]: this.gameMapper,
        [GameCategory.numbers]: this.virtualMapper,
        [GameCategory.sport]: this.virtualMapper,
    };

    public async getAllGames(): Promise<any> {
        const {
            casinoLive,
            virtuals,
            casinoDesktop, // in our app these are games
            casinoMobile, // games for mobile phones
        } = await this.restService.getAllGames();

        const result: {
            desktopGames: { [key in GameCategory]?: Game[]},
            mobileGames: { [key in GameCategory]?: Game[]},
            [GameCategory.casinoLive]: Game[];
            [GameCategory.sport]: Virtual[];
            [GameCategory.numbers]: Virtual[];
        } = {
            desktopGames: {},
            mobileGames: {},
            [GameCategory.casinoLive]: (this.casinoLiveMapper.map(casinoLive, true) as any) as Game[],
            [GameCategory.sport]: [],
            [GameCategory.numbers]: [],
        };

        // take a look of the endpoint structure, it will be much cleared
        const mapGames = (games: any, key?: 'desktopGames' | 'mobileGames') => {
            games.forEach(
                (category:
                    { name: string, games: Game[] }) => {
                    const name = category.name;
                    const categoryKey = mapCountryKey(name as any) as GameCategory;
                    const mapper = ((this.categoryMappers as any)[categoryKey] as any);
                    if (!categoryKey || !mapper) {
                        return;
                    }
                    const mappedGames = (mapper as any).map(category.games || [], true);

                    if (key) {
                        result[key][categoryKey] = mappedGames;
                    } else {
                        result[categoryKey as GameCategory.numbers | GameCategory.sport] = mappedGames;

                    }
                });
        };
        const isOnMobile = isMobile();
        mapGames(virtuals);
        result[GameCategory.sport] = this.filterNonDeviceGames(result[GameCategory.sport], isOnMobile);
        result[GameCategory.numbers] = this.filterNonDeviceGames(result[GameCategory.numbers], isOnMobile);

        if (isOnMobile) {
            mapGames(casinoDesktop, 'desktopGames');
        } else {
            mapGames(casinoMobile, 'mobileGames');
        }

        return result;
    }

    public async getFavouriteGames(): Promise<any> {
        const favouriteGamesResponse = await NetworkUtils.processResponse(this.restService.getFavouriteGames());
        return this.favoriteMapper.map(favouriteGamesResponse.data.Games, true);
    }

    public toggleFavouriteGame(gameId: number, add: boolean): Promise<void> {
        return NetworkUtils.processResponse(this.restService.toggleFavouriteGame(gameId, add));
    }

    public async getJackpotBanner() {
        const banner = await NetworkUtils.processResponse(this.restService.getJackpotBanner());

        if (!banner.data) {
            return null;
        }

        return this.jackpotBannerMapper.map(banner.data);
    }

    public async getXJackpotDetails(): Promise<XJackpotDetails | null> {
        const details = await NetworkUtils.processResponse(this.restService.getXJackpotDetails());

        if (!details.jackpotData) {
            return null;
        }

        return this.xJackpotDetailsMapper.map(details.jackpotData);
    }

    /**
     * @param externalId - game externalId
     * @param isFullGame - true if user is logged in and demo is not selected
     * @param isRealPlay - false if user is not logged in or demo is selected
     */
    public async launchCasinoGame(externalId: number, isFullGame: boolean, isRealPlay: boolean) {
        const response = await NetworkUtils.processResponse(
            this.restService.launchCasinoGame(externalId, isFullGame, isRealPlay)
        );

        return {
            url: response.data.Location as string,
            params: this.casinoGameLaunchParamsMapper.createTargetObject(response.data.Parameters),
        };
    }

    public async launchLiveLobby(category: string, gameManufacturer = 'EvolutionGaming Online Casino'): Promise<string> {
        const response = await NetworkUtils.processResponse(
            this.restService.launchLiveLobby(category, gameManufacturer)
        );
        return response.data.LobbyUrl;
    }

    private filterNonDeviceGames(games: Virtual[], isMobile: boolean) {
        return games.filter(
            (game) => isMobile ? game.fullMobileGameUrl : game.fullGameUrl,
        );
    }
}

export const gameService = new GameService();



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/game/GameService.ts